import { useMutation } from '@tanstack/react-query';
import { Wand2, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useCreateBlockMutation } from '@/hooks/queries/useCourseDetail';

/**
 * "Draft starter content with EduBorg" — Phase-5 fill-in pass.
 *
 * Visible on an empty lesson (instructor-only). Click → calls
 * /ai/spark-lesson-content for the lesson, then chains createBlock
 * mutations for each returned block in order. Blocks land in the editor
 * the same way hand-authored ones do, so the instructor can immediately
 * tweak.
 *
 * Cost is 2 AI credits regardless of how many blocks come back. In simulate
 * mode the API returns deterministic placeholder blocks so the flow is
 * exercisable without an API key.
 */
export function SparkLessonButton({
  token,
  moduleItemId,
}: {
  token: string;
  moduleItemId: string;
}) {
  const createBlock = useCreateBlockMutation(token, moduleItemId);

  const sparkMutation = useMutation({
    mutationFn: async () => {
      const result = await api.ai.sparkLessonContent(token, { moduleItemId });
      // Create the blocks one at a time. The position helper on the server
      // assigns positions in insertion order, so they end up in the order
      // EduBorg drafted them.
      for (const b of result.blocks) {
        await createBlock.mutateAsync({ type: b.type, payloadJson: b.payloadJson });
      }
      return result;
    },
  });

  return (
    <button
      type="button"
      onClick={() => void sparkMutation.mutateAsync()}
      disabled={sparkMutation.isPending}
      className="inline-flex items-center gap-1.5 rounded-xl border border-accent/30 bg-accent/5 px-4 py-2 text-sm font-semibold text-accent transition-colors hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {sparkMutation.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Wand2 className="h-4 w-4" />
      )}
      {sparkMutation.isPending ? 'Drafting blocks…' : 'Draft starter content with EduBorg'}
    </button>
  );
}
